// src/hooks/useQuantAnalysis.js - Quantitative portfolio analysis hook
//
// Results are cached in localStorage for an hour, keyed by user and holdings,
// so reloading the note does not spend one of a free user's daily runs.

import { useState, useCallback, useEffect, useMemo } from 'react';
import api from '../api';

const CACHE_TTL = 60 * 60 * 1000;

export function holdingsSignature(holdings) {
  return (holdings || [])
    .map((h) => `${String(h.ticker || '').toUpperCase()}:${h.quantity}`)
    .sort()
    .join('|');
}

export function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.at || Date.now() - parsed.at > CACHE_TTL) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

export function writeCache(key, data, extra = {}) {
  try {
    localStorage.setItem(key, JSON.stringify({ data, at: Date.now(), ...extra }));
  } catch (e) {
    // storage full or unavailable; the in-memory copy still works
  }
}

const useQuantAnalysis = (user, userProfile) => {
  const [quantData, setQuantData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [lastAnalysisTime, setLastAnalysisTime] = useState(null);
  const [hydratedKey, setHydratedKey] = useState(null);

  const hasMinHoldings = userProfile?.holdings?.length >= 1;

  const cacheKey = useMemo(() => {
    if (!user?.uid || !userProfile?.holdings) return null;
    return `dd_quant_${user.uid}_${holdingsSignature(userProfile.holdings)}`;
  }, [user?.uid, userProfile?.holdings]);

  // Hydrate from the persistent cache whenever the user or holdings change.
  useEffect(() => {
    if (!cacheKey) return;
    const cached = readCache(cacheKey);
    if (cached) {
      setQuantData(cached.data);
      setLastAnalysisTime(new Date(cached.at));
      if (cached.data?.usage) setRateLimitInfo(cached.data.usage);
    } else {
      setQuantData(null);
      setLastAnalysisTime(null);
    }
    setHydratedKey(cacheKey);
  }, [cacheKey]);

  const fetchQuantAnalysis = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && quantData && lastAnalysisTime) {
      if ((new Date() - lastAnalysisTime) < CACHE_TTL) {
        return quantData;
      }
    }

    if (!hasMinHoldings) {
      setError('At least 1 holding required for quant analysis');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/v1/portfolio/quant-analysis', {
        holdings: userProfile.holdings,
        user_profile: {
          ...userProfile,
          isPremium: user?.isPremium || false
        },
        user_id: user?.uid || null
      });

      setQuantData(response.data);
      setLastAnalysisTime(new Date());
      if (cacheKey) writeCache(cacheKey, response.data);

      if (response.data.usage) {
        setRateLimitInfo(response.data.usage);
      }

      return response.data;
    } catch (err) {
      console.error('Error running quant analysis:', err);

      if (err.response?.status === 429) {
        const detail = err.response.data.detail;
        setError(detail.message || 'Daily quant analysis limit reached');
        setRateLimitInfo({
          remaining: detail.remaining || 0,
          daily_limit: detail.daily_limit || 3,
          user_tier: detail.user_tier || 'free'
        });
      } else {
        const detail = err.response?.data?.detail;
        setError((typeof detail === 'string' ? detail : detail?.message) || err.message || 'Failed to run quant analysis');
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [quantData, lastAnalysisTime, hasMinHoldings, userProfile, user, cacheKey]);

  return {
    quantData,
    quantLoading: loading,
    quantError: error,
    quantRateLimitInfo: rateLimitInfo,
    quantHydrated: cacheKey !== null && hydratedKey === cacheKey,
    fetchQuantAnalysis: () => fetchQuantAnalysis(false),
    refreshQuantAnalysis: () => fetchQuantAnalysis(true),
    hasMinHoldings,
    lastAnalysisTime,
    clearQuantError: () => setError(null)
  };
};

export default useQuantAnalysis;
